import { useMutation, useQuery } from "@apollo/client";
import classNames from "classnames";
import { useFormik } from "formik";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Breadcrumb from "~/components/Breadcrumb";
import Button from "~/components/Button";
import Container from "~/components/Container";
import { HTML } from "~/components/dmca/content";
import Input from "~/components/Input";
import Layout from "~/layouts/ThemeLayout";
import Select from "~/components/Select";
import SEOTags from "~/components/SEOTags";
import Text from "~/components/Text";
import TextArea from "~/components/TextArea";
import { QUERY_GET_CMS_PAGE } from "~/data/cms/gql";
import { GET_CUSTOM_FORM_GQL, QUERY_GET_CUSTOM_FORM } from "~/data/home";
import { CustomForm, CustomFormDField } from "~/data/home/interface";
import { initializeSSRApollo } from "~/utils/apollo-client";
import { isValidEmail } from "~/utils/validators";
import { default as ReactSelect } from "react-select";
import MultipleSelect from "~/components/MultipleSelect";
import { isEmpty } from "lodash";
import Skeleton from "react-loading-skeleton";
const IntellactualForm = ({
  data,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const router = useRouter();

  const [siteName, setSiteName] = useState("");
  const { data: formdata, loading } = useQuery(QUERY_GET_CUSTOM_FORM, {
    variables: {
      id: Number(process.env.NEXT_PUBLIC_INTELLECTUAL as string),
    },
    context: { fetchOptions: { method: "GET" } },
  });

  useEffect(() => {
    function capitalizeFirstLetter(string: string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    }

    setSiteName(capitalizeFirstLetter(window.location.hostname));
  }, [siteName]);

  const customForm: CustomForm = useMemo(() => {
    return formdata?.customForm || [];
  }, [formdata]);

  function convertStringToJson(jsonString: string) {
    try {
      const jsonData = JSON.parse(jsonString);
      return jsonData;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null;
    }
  }
  const pagedate = useMemo(() => {
    return convertStringToJson(data?.cmsPage?.content || "");
  }, [data]);

  const initialValuesFields: any = {};
  customForm?.fields?.forEach((i: any) => {
    initialValuesFields[i.code] = null;
  });
  const [submitForm, { loading: isSignUpLoading }]: any = useMutation(
    GET_CUSTOM_FORM_GQL,
    {
      onCompleted: () => {
        toast.success("Submit Form successful");
        formik.resetForm();
      },
      onError: ({ message }) => {
        toast.error(message);
      },
    }
  );

  const formik = useFormik({
    initialValues: initialValuesFields,
    validateOnChange: true,
    validateOnBlur: false,
    validate(values: any) {
      const errors: Record<string, string> = {};
      customForm.fields?.map((field: CustomFormDField) => {
        if (!!field.is_required && isEmpty(values[field.code])) {
          errors[field.code] = `${field.title} is required`;
        } else {
          if (
            field.validator === "validate-email" &&
            !isValidEmail(values[field.code])
          ) {
            errors[field.code] = `Please enter a valid email address`;
          }
        }
      });
      return errors;
    },
    onSubmit: async (values) => {
      const fields: any = [];
      const fields_multi: any = [];
      const errors: Record<string, string> = {};
      customForm.fields?.map((field: CustomFormDField) => {
        if (field.field_type !== "multiselect") {
          fields.push({
            key: field.code,
            value: values?.[field.code],
          });
        } else if (
          field.validator === "validate-email" &&
          !isValidEmail(values[field.code])
        ) {
          errors[field.code] = `Please enter a valid email address`;
        } else if (field.field_type === "multiselect") {
          fields_multi.push({
            key: field.code,
            value: values?.[field.code]?.map((item: any) => item.value),
          });
        }
      });
      return await submitForm({
        variables: {
          form_id: Number(process.env.NEXT_PUBLIC_INTELLECTUAL),
          fields: fields,
          fields_multi: fields_multi,
        },
      });
    },
  });

  const { meta_description, meta_title, meta_keywords, url_key, title } =
    data?.cmsPage || {};

  // Nội dung HTML ban đầu
  let html = data?.cmsPage?.content;

  // Tách các phần tử bằng cách sử dụng biểu thức chính quy (RegExp)
  let topContent = html.match(/<div id="position-top">[\s\S]*?<\/div>/)?.[0];
  let bottomContent = html.match(
    /<div id="position-bottom">[\s\S]*?<\/div>/
  )?.[0];

  return (
    <Layout isUseMinHeight>
      <SEOTags
        title={meta_title || formdata?.customForm?.name}
        description={meta_description}
        keywords={meta_keywords}
        url={url_key}
      />
      <Container
        rootClassName="py-5 lg:py-8"
        className="flex flex-col gap-4 cms-page"
      >
        <Breadcrumb items={[{ title: "Home", path: "/" }, { title: title }]} />
        {topContent ? (
          <div
            dangerouslySetInnerHTML={{
              __html: topContent || "",
            }}
          />
        ) : (
          ""
        )}
        <div>
          {!!loading && (
            <div className="grid grid-cols-2 gap-4 flex-1 max-w-[780px]:">
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
              <Skeleton height={42} className="w-full" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 flex-1 max-w-[780px]:">
            {!!customForm?.fields?.length && !loading
              ? customForm?.fields?.map((item: any, index: number) => {
                const isHtml =
                  HTML.find(({ key }) => key === item?.position) !==
                  undefined;

                return (
                  <Fragment key={index}>
                    <div
                      className={classNames({
                        "col-span-2": item?.field_type === "textarea",
                      })}
                    >
                      {renderFiled(item, formik)}
                      {item?.note
                        ? renderHtmlContent({ content: item?.note })
                        : ""}
                    </div>
                  </Fragment>
                );
              })
              : ""}
          </div>
          <div className="">
            {!!customForm?.fields?.length && (
              <div className="mt-2 text-right w-full mr-auto">
                <Button
                  loading={isSignUpLoading}
                  onClick={() => formik.handleSubmit()}
                  className="w-full"
                >
                  Submit Contact
                </Button>
              </div>
            )}
          </div>
        </div>

        <div
          dangerouslySetInnerHTML={{
            __html: bottomContent || "",
          }}
        />
      </Container>
    </Layout>
  );
};

export default IntellactualForm;
export async function getServerSideProps(req: GetServerSidePropsContext) {
  const apolloClient = initializeSSRApollo({
    httpLinkProps: { useGETForQueries: true },
  });

  let res = {} as CmsPageProps;
  try {
    const data = await apolloClient.query({
      query: QUERY_GET_CMS_PAGE,
      variables: {
        identifier: "intellectual-property-claim",
      },
      context: { fetchOptions: { method: "GET" } },
    });

    res = { ...data };
  } catch (error) {
    console.error(error, "QUERY_SEO_CATEGORY");
  }
  return { props: { ...res } };
}

export const renderHtmlContent = ({
  key = null,
  content = "",
  style = {},
  isDetectLink = false,
  isReplaceVideoEmbed = false,
  id = "",
}): any => {
  try {
    let formattedContent = replaceNewLine(content);

    if (!!isDetectLink) formattedContent = autoDetectUrl(formattedContent);

    const inheritStyle = {
      fontSize: "inherit",
      fontWeight: "inherit",
      lineHeight: "inherit",
    } as any;

    return (
      <div
        id={id}
        key={key ? key : undefined}
        style={Object.assign({}, inheritStyle, style)}
        dangerouslySetInnerHTML={{
          __html: formattedContent,
        }}
      />
    );
  } catch (e) {
    return "";
  }
};

export const replaceNewLine = (content: string) =>
  content
    .replace(/\n\n/g, "\n")
    .replace(/\r\n\r\n/g, "\n")
    .replace(/\n/g, "");

export const autoDetectUrl = (content: string) => {
  try {
    var urlRegex = /(https?:\/\/[^\s]+)/g;
    const style = "color: #1887db; font-size: inherit;";

    return content.replace(urlRegex, function (url) {
      return ` <a style="${style}" href="${url}">${url}</a> `;
    });
  } catch (e) {
    return "";
  }
};
const renderFiled = (field: CustomFormDField, formik: any) => {
  switch (field?.field_type) {
    case "textarea":
      return (
        <div>
          <TextArea
            {...formik.getFieldProps(`${field.code}`)}
            error={
              (!!field?.code &&
                //   formik.touched?.[field?.code] &&
                !!formik?.errors?.[field.code] &&
                (formik?.errors?.[field.code] as string)) ||
              ""
            }
            label={field?.title}
            rows={4}
            required={field?.is_required}
            className="w-ful"
          />
        </div>
      );
    case "select":
      return (
        <div>
          <Select
            value={formik?.values?.[field.code]}
            options={
              field?.options?.map((i) => ({
                value: i.key,
                label: i.value,
              })) || []
            }
            {...formik.getFieldProps(`${field.code}`)}
            onChange={(value) => formik.setFieldValue(field.code, value)}
            label={field?.title}
            rows={4}
            required={field?.is_required}
            error={
              (!!field?.code &&
                //   formik.touched?.[field?.code] &&
                !!formik?.errors?.[field.code] &&
                (formik?.errors?.[field.code] as string)) ||
              ""
            }
          />
        </div>
      );
    case "multiselect":
      return (
        <div className="col-span-2">
          <MultipleSelect
            isMulti
            options={
              field?.options?.map((i) => ({
                value: i.key,
                label: i.value,
              })) || []
            }
            {...formik.getFieldProps(`${field.code}`)}
            value={formik?.values?.[field.code] || []}
            onChange={(value: any) => formik.setFieldValue(field.code, value)}
            label={field?.title}
            required={field?.is_required}
            error={
              (!!field?.code &&
                //   formik.touched?.[field?.code] &&
                !!formik?.errors?.[field.code] &&
                (formik?.errors?.[field.code] as string)) ||
              ""
            }
          />
        </div>
      );
    default:
      return (
        <div>
          <Input
            type={field?.field_type}
            label={field?.title}
            required={field?.is_required}
            {...formik.getFieldProps(`${field.code}`)}
            error={
              (!!field?.code &&
                //   formik.touched?.[field?.code] &&
                !!formik?.errors?.[field.code] &&
                (formik?.errors?.[field.code] as string)) ||
              ""
            }
          />
        </div>
      );
  }
};
